'use client';

import { Box } from '@chakra-ui/react';
import { usePathname, useSearchParams } from 'next/navigation';
import { useRouter } from 'next/navigation';
import { useQueryState } from 'nuqs';
import React from 'react';

import { useClearAllFilters } from '@/hooks/filters/clear-all-filters.hook';
import {
  BodyTypeList,
  ColourTypeList,
  Consumption,
  DealershipsByProvince,
  DoorTypeList,
  DriveTypeList,
  FeatureTypeList,
  FuelTypeList,
  MileageType,
  TransmissionTypeList,
  YearType,
} from '@/services/filters/new-filters.service';
import { useIsMobile } from '@/utils/useIsMobile';

import {
  getFilterCounts,
  Params,
} from '../../search/search-results/search-params';

import { NewBody } from './new-body/new-body';
import { NewFeatures } from './new-features/new-features';
import styles from './new-filters.module.scss';
import { NewLocation } from './new-location/new-location';
import { NewMoreFilters } from './new-more-filters/new-more-filters';
import { NewPrices } from './new-prices/new-prices';
import { NewYearsMileage } from './new-years-mileage/new-years-mileage';

export interface NewFiltersProps {
  maxPrice: number;
  bodyTypes: BodyTypeList;
  colourTypes: ColourTypeList;
  doorTypes: DoorTypeList;
  defaultFuelConsumption: Consumption;
  transmissionTypes: TransmissionTypeList;
  driveTypes: DriveTypeList;
  fuelTypes: FuelTypeList;
  years: YearType;
  mileages: MileageType;
  dealershipsByProvince: DealershipsByProvince;
  params: Params;
  defaultFilters: DefaultFilters;
  features: FeatureTypeList;
  isLocationHidden?: boolean;
}

export type DefaultFilters = {
  defaultMake?: string;
  defaultModel?: string;
  defaultLocation?: string;
  defaultMaxPrice?: number;
  defaultDealershipId?: string;
};

const NON_FILTER_PARAMS = new Set([
  'type',
  'nonce',
  'page',
]);

function hasFiltersOtherThanType(searchParams: URLSearchParams) {
   for (const key of searchParams.keys()) {
    if (!NON_FILTER_PARAMS.has(key)) {
      return true; 
    }
  }
  return false; 
}

export default function NewFilters({
  bodyTypes,
  colourTypes,
  doorTypes,
  defaultFuelConsumption,
  maxPrice,
  dealershipsByProvince,
  transmissionTypes,
  driveTypes,
  fuelTypes,
  years,
  mileages,
  params,
  defaultFilters,
  features,
  isLocationHidden,
}: NewFiltersProps) {
  const [searchQuery, setSearchQuery] = useQueryState('search');
  const filterCounts = getFilterCounts(params);
  const { clearAll } = useClearAllFilters();
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();
  const hasClearableFilters = hasFiltersOtherThanType(searchParams);
  const isSearchPage = pathname.includes('/used-cars/search');
  const isMobile = useIsMobile();

  return (
    <Box>
      <Box className={styles.filters}>
        {/* LEFT: FILTERS */}
        <Box display="flex" flexWrap="wrap" gap={{ base: '16px', xl: '5px' }}
          mt={{ base: 4, lg: 0 }}>
          {/* <MakeModelSearchFilter
            brandsAndRanges={brandsAndRanges}
            defaultMake={defaultFilters.defaultMake}
            defaultModel={defaultFilters.defaultModel}
            searchQuery={searchQuery || undefined}
            setSearchQuery={setSearchQuery}
          /> */}

          <NewBody
            bodyTypes={bodyTypes}
            colourTypes={colourTypes}
            doorTypes={doorTypes}
            count={filterCounts.body}
            searchQuery={searchQuery || undefined}
            setSearchQuery={setSearchQuery}
          />

          {!isLocationHidden && (
            <NewLocation
              dealershipsByProvince={dealershipsByProvince}
              count={filterCounts.location}
              defaultLocation={defaultFilters?.defaultLocation}
              defaultDealershipId={defaultFilters?.defaultDealershipId}
              searchQuery={searchQuery || undefined}
              setSearchQuery={setSearchQuery}
            />
          )}

          <NewPrices
            count={filterCounts.price}
            maxPrice={maxPrice}
            defaultMaxPrice={defaultFilters.defaultMaxPrice}
          />

          <NewYearsMileage
            count={filterCounts.yearsAndMileage}
            years={years}
            mileages={mileages}
          />

          <NewFeatures
            count={filterCounts.features}
            featureCounts={features}
          />

          <NewMoreFilters
            defaultFuelConsumption={defaultFuelConsumption}
            transmissionTypes={transmissionTypes}
            driveTypes={driveTypes}
            fuelTypes={fuelTypes}
            count={filterCounts.more}
            searchQuery={searchQuery || undefined}
            setSearchQuery={setSearchQuery}
          />
        </Box>
      </Box>

      {hasClearableFilters && (
        <Box mt={4} display="flex" gap={3}>
          <button
            type="button"
            onClick={clearAll}
            className={styles.clearAllBtn}
            data-cy="clear-all-filters-button"
          >
            Clear all filters
          </button>
          {!isSearchPage && !isMobile && (
            <button
              type="button"
              onClick={() => router.push('/used-cars/search')}
              className={styles.viewAllBtn}
              data-cy="view-all-used-cars-button"
            >
              View All Used Cars
            </button>
          )}
        </Box>
      )}
      
      {/* Show View All button even when no filters are present on search page */}
      {!hasClearableFilters && !isSearchPage && !isMobile && (
        <Box mt={4}>
          <button
            type="button"
            onClick={() => router.push('/used-cars/search')}
            className={styles.viewAllBtn}
            data-cy="view-all-used-cars-button"
          >
            View All Used Cars
          </button>
        </Box>
      )}
    </Box>
  );
}
